import { FormEvent, SyntheticEvent, useEffect, useState } from "react";
import {
  Grid,
  Stack,
  Dialog,
  Button,
  Typography,
  IconButton,
  FormControl,
  DialogTitle,
  DialogActions,
  DialogContent
} from "@material-ui/core";
import { Close } from "@material-ui/icons";
import { LoadingButton } from "@material-ui/lab";
import { useTranslation } from "react-i18next";
import { PlaceAutoComplete } from "../listings/table/filters/PlaceAutocomplete";
import { AlgoliaAutoComplete } from "../listings/table/filters/AlgoliaAutocomplete";
import { ProcessedListingAddress } from "../../../@types/sharedSchema";
import { ListingInteractiveMap } from "../listings/modal/core/ListingInteractiveMap";
import type { DialogProps } from "@material-ui/core";
import type { ListingInteractiveMapBaseProps } from "../listings/modal/core/ListingInteractiveMap";

export type AutocompleteType = "place" | "algolia";

type CreateAddressDialogProps = Pick<DialogProps, "open"> & {
  address: ProcessedListingAddress | null;
  autocompleteType: AutocompleteType;
  listingInteractiveMapBaseProps?: ListingInteractiveMapBaseProps;
  onClose: () => void;
  onConfirm: (data: CreateAddressDetails) => void;
};

export type CreateAddressDetails = {
  city: string;
  address: string;
  neighborhood: string;
};

const initialCreateAddressForm: CreateAddressDetails = {
  city: "",
  address: "",
  neighborhood: ""
};

export function CreateAddressDialog({
  open,
  address,
  autocompleteType = "place",
  listingInteractiveMapBaseProps,
  onClose,
  onConfirm
}: CreateAddressDialogProps): JSX.Element {
  const [createAddressForm, setCreateAddressForm] = useState<CreateAddressDetails>(
    setInitialCreateAddressForm(address)
  );

  const { t: translate } = useTranslation();

  useEffect(() => {
    if (!address) return;

    setCreateAddressForm(setInitialCreateAddressForm(address));
  }, [address]);

  const handleAutoCompleteChange =
    (type: keyof CreateAddressDetails) =>
    (_event: SyntheticEvent, newInputValue: string): void => {
      setCreateAddressForm({
        ...createAddressForm,
        [type]: newInputValue
      });
    };

  const handleAutoCompleteFill = (data: Partial<CreateAddressDetails>): void => {
    setCreateAddressForm({
      ...createAddressForm,
      ...data
    });
  };

  const handleSubmit = (e: FormEvent<HTMLFormElement>): void => {
    e.preventDefault();
    e.stopPropagation();

    onConfirm(createAddressForm);

    setCreateAddressForm(initialCreateAddressForm);

    onClose();
  };

  let TargetAutocomplete = PlaceAutoComplete;

  if (autocompleteType === "algolia") {
    TargetAutocomplete = AlgoliaAutoComplete;
  }

  return (
    <Dialog fullWidth open={open} maxWidth="sm" onClose={onClose}>
      <DialogTitle sx={{ py: 2 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">
            {address
              ? translate("listingsMatch.createAddressDialog.edit")
              : translate("listingsMatch.createAddressDialog.create")}
          </Typography>
          <IconButton onClick={onClose}>
            <Close />
          </IconButton>
        </Stack>
      </DialogTitle>
      <FormControl sx={{ overflow: "hidden" }} component="form" onSubmit={handleSubmit}>
        <DialogContent>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TargetAutocomplete
                type="address"
                required
                inputValue={createAddressForm.address}
                handleInputChange={handleAutoCompleteChange("address")}
                handleAutoCompleteFill={handleAutoCompleteFill}
              />
            </Grid>
            <Grid item xs={12}>
              <TargetAutocomplete
                type="neighborhood"
                inputValue={createAddressForm.neighborhood}
                handleInputChange={handleAutoCompleteChange("neighborhood")}
                handleAutoCompleteFill={handleAutoCompleteFill}
              />
            </Grid>
            <Grid item xs={12}>
              <TargetAutocomplete
                type="city"
                required
                inputValue={createAddressForm.city}
                handleInputChange={handleAutoCompleteChange("city")}
              />
            </Grid>
            {listingInteractiveMapBaseProps && (
              <Grid item xs={12}>
                <ListingInteractiveMap
                  allowDefaultCoordinates
                  {...listingInteractiveMapBaseProps}
                />
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions sx={{ justifyContent: "center" }}>
          <Button variant="contained" color="error" onClick={onClose}>
            {translate("contactsPage.control.cancel")}
          </Button>
          <LoadingButton type="submit" variant="contained" color="primary">
            {translate("contactsPage.control.save")}
          </LoadingButton>
        </DialogActions>
      </FormControl>
    </Dialog>
  );
}

function setInitialCreateAddressForm(
  address: ProcessedListingAddress | null
): CreateAddressDetails {
  const { street, city, neighborhood } = address ?? {};

  return {
    city: city ?? "",
    address: street ?? "",
    neighborhood: neighborhood ?? ""
  };
}
